import axios, { AxiosInstance } from 'axios';
import { getApiUrl } from './apiUrl';

// Create axios instance with base configuration
const api: AxiosInstance = axios.create({
  baseURL: getApiUrl(),
  timeout: 10000, // 10 second timeout
});

// User role validation
const getUserRole = (): string | null => {
  try {
    const token = sessionStorage.getItem('token');
    if (!token) return null;
    
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.role || null; // Don't default to any role
  } catch (error) {
    console.warn('Failed to parse token for role validation');
    return null;
  }
};

// Route validation function
const validateRouteAccess = (url: string, userRole: string | null): boolean => {
  if (!userRole) return false;
  
  // Admin routes that require admin role
  const adminRoutes = ['/api/admin/', '/api/super-admin/', '/api/auth/heartbeat', '/api/auth/logout'];
  
  // Client routes that require client role
  const clientRoutes = ['/api/client/', '/api/auth/client/'];
  
  // Routes that Super Admin Viewer can access (client routes + assignments)
  const superAdminViewerRoutes = ['/api/assignments', '/api/upload/', '/api/companies/'];
  
  const isAdminRoute = adminRoutes.some(route => url.includes(route));
  const isClientRoute = clientRoutes.some(route => url.includes(route));
  const isSuperAdminViewerRoute = superAdminViewerRoutes.some(route => url.includes(route));
  
  // Super Admin Viewer can access client routes and assignment routes
  if (userRole === 'super_admin_viewer') {
    if (isAdminRoute) {
      console.error(`🚫 Super Admin Viewer attempted to access admin route: ${url}`);
      return false;
    }
    // Super Admin Viewer can access client routes and assignment routes
    return isClientRoute || isSuperAdminViewerRoute || url.includes('/api/');
  }
  
  if (isAdminRoute && userRole !== 'admin') {
    console.error(`🚫 Client attempted to access admin route: ${url}`);
    return false;
  }
  
  if (isClientRoute && !['client', 'editor', 'viewer', 'admin', 'super_admin_viewer'].includes(userRole)) {
    console.error(`🚫 Invalid role attempted to access client route: ${url}`);
    return false;
  }
  
  return true;
};

// Request interceptor for authentication and validation
api.interceptors.request.use(
  (config) => {
    // Handle Super Admin Viewer tokens first (same logic as AuthContext)
    const tempToken = sessionStorage.getItem('temp_client_token');
    const isTemporaryAccess = window.location.search.includes('temp_access=true');
    
    
    if (tempToken) {
      // Verify the token is actually for temporary access
      try {
        const tokenParts = tempToken.split('.');
        if (tokenParts.length === 3) {
          const base64Payload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
          const tokenPayload = JSON.parse(atob(base64Payload));
          
          // Only use temporary token if it's actually marked as temporary
          if (tokenPayload.isTemporary || tokenPayload.isSuperAdminViewer) {
            config.headers.Authorization = `Bearer ${tempToken}`;
            
            // Validate route access based on user role
            const userRole = tokenPayload.role || 'super_admin_viewer';
            const url = config.url || '';
            
            if (!validateRouteAccess(url, userRole)) {
              throw new Error(`Access denied: ${userRole || 'unknown'} role cannot access this route`);
            }
            return config;
          }
        }
      } catch (error) {
        // Failed to validate temporary token, falling back to regular token
      }
    }
    
    // Use regular token for all other cases
    const token = sessionStorage.getItem('token');
    
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Validate route access based on user role
    const userRole = getUserRole();
    const url = config.url || '';
    
    
    if (!validateRouteAccess(url, userRole)) {
      throw new Error(`Access denied: ${userRole || 'unknown'} role cannot access this route`);
    }
    return config;
  },
  (error) => {
    console.error('API Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 403 && error.response?.data?.error?.includes('token cannot access')) {
      console.error('Role-based access denied:', error.response.data.message);
      // Clear token if there's a role mismatch
      sessionStorage.removeItem('token');
      window.location.href = '/admin/login';
    }
    
    return Promise.reject(error);
  }
);

// API functions for different user roles
export const clientAPI = {
  // Get client dashboard data
  getClientDashboard: () => api.get('/api/client/dashboard'),
  
  // Get client data (Sheet 1 only)
  getClientData: () => api.get('/api/upload/client/data?sheet=1'),
  
  // Get editor data (Sheet 1 only with editor columns)
  getEditorData: () => api.get('/api/upload/editor/data'),
  
  // Get buying intent data
  getBuyingIntentData: () => api.get('/api/upload/client/buying-intent'),
  
  // Get assignments
  getAssignments: (params?: any) => api.get('/api/assignments', { params }),
  
  // Get editor assignments (only active assignments for current editor)
  getEditorAssignments: () => api.get('/api/assignments/editor/me'),
  
  // Get viewer assignments (all assignments for current viewer - active and completed)
  getViewerAssignments: () => api.get('/api/assignments/viewer/me'),
  
  // Get individual completed leads across all assignments
  getCompletedLeads: () => api.get('/api/assignments/completed-leads'),
  
  // Delete a completed lead
  deleteCompletedLead: (leadEditId: string) => api.delete(`/api/assignments/completed-leads/${leadEditId}`),
  
  // Get leads for an assignment
  getAssignmentLeads: (assignmentId: string) => api.get(`/api/assignments/${assignmentId}/leads`),
  
  // Submit individual lead completion
  submitLead: (assignmentId: string, leadId: string, data: any) => 
    api.post(`/api/assignments/${assignmentId}/leads/${leadId}/submit`, data),
  
  // Complete an assignment
  completeAssignment: (assignmentId: string) => api.post(`/api/assignments/${assignmentId}/complete`),
  
  // Get notifications
  getNotifications: () => api.get('/api/assignments/notifications'),
  
  // Mark notification as read
  markNotificationRead: (id: number) => api.patch(`/api/assignments/notifications/${id}/read`),
  
  // Mark all notifications as read
  markAllNotificationsRead: () => api.patch('/api/assignments/notifications/mark-all-read'),
  
  // Update company status
  updateCompanyStatus: (assignmentId: string, companyId: string, status: string) => 
    api.patch(`/api/assignments/${assignmentId}/company/${companyId}/status`, { status }),
  
  // Submit completed work
  submitWork: (data: any) => api.post('/api/assignments/submit-work', data),
  
  // Create assignment
  createAssignment: (data: any) => api.post('/api/assignments', data),
};

export const superAdminAPI = {
  rebuildDashboards: (payload: { allCompanies: boolean; companyIds?: string[] }) =>
    api.post('/api/super-admin/dashboard/rebuild', payload),
  getDashboardRuns: () => api.get('/api/super-admin/dashboard/runs'),
  getRunDiffs: (runId: string) => api.get(`/api/super-admin/dashboard/runs/${runId}/diffs`),
  resolveRunResults: (
    runId: string,
    action: 'accept' | 'reject',
    options?: { companyIds?: string[]; notes?: string }
  ) => api.post(`/api/super-admin/dashboard/runs/${runId}/resolve`, { action, ...(options || {}) }),
  deleteDashboardRun: (runId: string) => api.delete(`/api/super-admin/dashboard/runs/${runId}`),
  deleteAllDashboardRuns: () =>
    api.delete('/api/super-admin/dashboard/runs', {
      params: { all: true }
    }),
  reprocessBuyingIntents: (payload: { allCompanies: boolean; companyIds?: string[] }) =>
    api.post('/api/super-admin/buying-intent/reprocess', payload)
};

// Heartbeat API instance for authentication and status updates
export const heartbeatApi = api;

export default api;